import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { auth } from "@/lib/endpoints";
import { env } from "@/lib/env";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/lib/api";

export function LoginPage() {
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const { isAuthenticated, isLoading, refetch } = useAuth();

  useEffect(() => {
    document.title = "Sign in — GetCitizen AI";
  }, []);

  const [testEmail, setTestEmail] = useState("");

  // After test-login succeeds, refetch /v1/me so isAuthenticated flips
  // to true; the <Navigate /> guard below then bounces the user to
  // `next`. Doing it via the auth state instead of a manual navigate()
  // avoids the race where ProtectedRoute would see the stale (cached)
  // `isAuthenticated=false` and redirect us back to /login.
  const testLogin = useMutation({
    mutationFn: (email: string) => auth.testLogin(email),
    onSuccess: () => {
      void refetch();
    },
  });

  if (!isLoading && isAuthenticated) {
    return <Navigate to={next === "/" ? "/dashboard" : next} replace />;
  }

  const handleGoogle = () => {
    document.cookie = `post_login=${encodeURIComponent(next)}; path=/; max-age=600; samesite=lax`;
    window.location.href = auth.loginUrl();
  };

  const googleEnabled = env.googleOAuthEnabled;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-slate-50/50">
      {/* Logo */}
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shadow-sm">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 1v14M1 8h14M4.5 4.5l7 7M11.5 4.5l-7 7" />
          </svg>
        </div>
        <span className="text-xl font-bold tracking-tight font-display text-slate-900">
          GetCitizen <span className="text-blue-600">AI</span>
        </span>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200/80 shadow-card p-8">
        <div className="text-center mb-7">
          <h1 className="text-2xl font-bold font-display text-slate-900">Sign in to GetCitizen AI</h1>
          <p className="text-slate-500 mt-1.5 text-sm leading-relaxed">
            We&apos;ll save your progress so you can pick up where you left off.
          </p>
        </div>

        {/* Google sign-in. In closed-alpha (VITE_ENABLE_GOOGLE_OAUTH≠true)
            the button stays visible but disabled with a "Coming soon" hint
            so the visual identity is preserved while we collect real
            applicant feedback through seeded test users. */}
        <button
          type="button"
          onClick={googleEnabled ? handleGoogle : undefined}
          disabled={!googleEnabled}
          title={googleEnabled ? "" : "Coming soon — closed alpha"}
          className={
            "w-full inline-flex items-center justify-center gap-2.5 rounded-full px-4 py-3 text-sm font-semibold transition-all duration-200 shadow-sm " +
            (googleEnabled
              ? "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md hover:-translate-y-0.5"
              : "bg-slate-200 text-slate-500 cursor-not-allowed")
          }
        >
          {/* Google G */}
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
            <path fill="currentColor" fillOpacity="0.9" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.715v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.613z" />
            <path fill="currentColor" fillOpacity="0.8" d="M9 18c2.43 0 4.467-.806 5.956-2.182l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
            <path fill="currentColor" fillOpacity="0.7" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
            <path fill="currentColor" fillOpacity="0.9" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
          </svg>
          {googleEnabled ? "Continue with Google" : "Continue with Google · Coming soon"}
        </button>

        {/* Test-user login form. Hidden when Google OAuth is on. */}
        {!googleEnabled && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const email = testEmail.trim().toLowerCase();
              if (!email) return;
              testLogin.mutate(email);
            }}
            className="mt-6 pt-5 border-t border-dashed border-slate-200 space-y-3"
          >
            <p className="text-xs font-medium text-slate-500 text-center">
              Closed alpha — sign in with your invite email
            </p>
            <input
              type="email"
              required
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
            />
            <button
              type="submit"
              disabled={testLogin.isPending}
              className="w-full rounded-full bg-slate-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-800 disabled:bg-slate-300"
            >
              {testLogin.isPending ? "Signing in…" : "Sign in"}
            </button>
            {testLogin.isError && (
              <p className="text-xs text-red-600 text-center">
                {(testLogin.error as ApiError)?.status === 404
                  ? "That email isn't on the alpha list yet."
                  : (testLogin.error as Error).message ||
                    "Could not sign in — try again."}
              </p>
            )}
          </form>
        )}

        <p className="text-xs text-slate-400 mt-5 text-center leading-relaxed">
          By continuing you agree to our{" "}
          <Link to="/terms" className="underline underline-offset-2 hover:text-slate-600 transition-colors">Terms</Link>
          {" "}and{" "}
          <Link to="/privacy" className="underline underline-offset-2 hover:text-slate-600 transition-colors">Privacy Policy</Link>.
          We never sell your information.
        </p>
      </div>

      <p className="text-xs text-slate-400 mt-6">
        © {new Date().getFullYear()} GetCitizen AI
      </p>
    </div>
  );
}
